/**
 * Centralized seed data for the MineGuard demo dashboard.
 * Types loosely match Drizzle schema output shapes so presentation
 * components can be shared between real and demo dashboards.
 */

// ── Site ─────────────────────────────────────────────────────────────────────

export const DEMO_SITE = {
  id: "demo-site-mahdia",
  name: "Mahdia Operations",
  location: "Mahdia, Region 8",
  workspaceId: "demo-workspace",
  ownerId: "demo-user",
  timezone: "America/Guyana",
  createdAt: new Date("2026-03-01"),
  nodes: 3,
  lastSync: "4 min ago",
}

// ── Engines ──────────────────────────────────────────────────────────────────

export const DEMO_ENGINES = [
  {
    id: "demo-eng-gen",
    siteId: DEMO_SITE.id,
    engineId: "generator",
    name: "Generator #1",
    engineType: "Deutz BF4M2012",
    enrolledAt: new Date("2026-03-01"),
    siteName: DEMO_SITE.name,
    fuelRate: 12, // L/hr at mining load
  },
  {
    id: "demo-eng-pump",
    siteId: DEMO_SITE.id,
    engineId: "waterpump",
    name: "Water Pump",
    engineType: "Honda WX10",
    enrolledAt: new Date("2026-03-01"),
    siteName: DEMO_SITE.name,
    fuelRate: 4.5,
  },
  {
    id: "demo-eng-sluice",
    siteId: DEMO_SITE.id,
    engineId: "sluicepump",
    name: "Sluice Pump",
    engineType: "Robin EY20",
    enrolledAt: new Date("2026-03-02"),
    siteName: DEMO_SITE.name,
    fuelRate: 6,
  },
  {
    id: "demo-eng-exc",
    siteId: DEMO_SITE.id,
    engineId: "excavator",
    name: "Excavator",
    engineType: "Deutz TD2.9",
    enrolledAt: new Date("2026-03-02"),
    siteName: DEMO_SITE.name,
    fuelRate: 14,
  },
]

// ── Engine events (latest state per engine) ──────────────────────────────────

const now = new Date()

export const DEMO_LATEST_EVENTS = new Map([
  [`generator:${DEMO_SITE.id}`, { state: "on" as const, confidence: 0.97, timestamp: new Date(now.getTime() - 4 * 60000) }],
  [`waterpump:${DEMO_SITE.id}`, { state: "on" as const, confidence: 0.94, timestamp: new Date(now.getTime() - 4 * 60000) }],
  [`sluicepump:${DEMO_SITE.id}`, { state: "off" as const, confidence: 0.91, timestamp: new Date(now.getTime() - 2 * 3600000) }],
  [`excavator:${DEMO_SITE.id}`, { state: "off" as const, confidence: 0.89, timestamp: new Date(now.getTime() - 4 * 3600000) }],
])

// ── Today's runtime (hours) ──────────────────────────────────────────────────

export const DEMO_TODAY_HOURS = {
  generator: 6.38,
  waterpump: 5.78,
  sluicepump: 0,
  excavator: 0,
}

export const DEMO_WEEK_HOURS = {
  generator: 52.1,
  waterpump: 44.3,
  sluicepump: 38.7,
  excavator: 31.2,
}

// ── Timeline Events ──────────────────────────────────────────────────────────
// Event-based: precise start/end times per engine per day.
// NOT hourly blocks — actual engine start/stop timestamps.

export interface EngineRun {
  engineId: string
  engineName: string
  start: number   // minutes from midnight (e.g., 362 = 6:02 AM)
  end: number     // minutes from midnight (e.g., 1007 = 4:47 PM)
  anomaly?: boolean
}

export interface DayEvents {
  date: string     // "Mar 27"
  dayNum: number   // 27
  weekday: string  // "Thu"
  runs: EngineRun[]
  totalHours: number
  hasAlert: boolean
}

function mins(h: number, m: number) { return h * 60 + m }
function fmtTime(totalMins: number) {
  const h = Math.floor(totalMins / 60)
  const m = totalMins % 60
  const ampm = h >= 12 ? "PM" : "AM"
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`
}
export { fmtTime }

function dur(start: number, end: number) {
  const d = end - start
  return Math.round(d / 6) / 10  // hours, 1 decimal
}

// Standard workday pattern (small variations each day)
function workday(dayOffset: number): EngineRun[] {
  return [
    { engineId: "generator", engineName: "Generator #1", start: mins(6, 2 + dayOffset), end: mins(16, 47 - dayOffset * 2) },
    { engineId: "waterpump", engineName: "Water Pump", start: mins(7, 15 + dayOffset), end: mins(15, 32 - dayOffset) },
    { engineId: "sluicepump", engineName: "Sluice Pump", start: mins(7, 8 + dayOffset * 2), end: mins(14, 55 - dayOffset * 3) },
    { engineId: "excavator", engineName: "Excavator", start: mins(7, 22 + dayOffset), end: mins(11, 45 + dayOffset * 2) },
  ]
}

function halfday(dayOffset: number): EngineRun[] {
  return [
    { engineId: "generator", engineName: "Generator #1", start: mins(6, 30 + dayOffset), end: mins(12, 15 - dayOffset) },
    { engineId: "waterpump", engineName: "Water Pump", start: mins(7, 10 + dayOffset), end: mins(11, 40 - dayOffset * 2) },
  ]
}

function makeDayEvents(dayNum: number, weekday: string, runs: EngineRun[], hasAlert: boolean): DayEvents {
  const totalHours = runs.reduce((sum, r) => sum + dur(r.start, r.end), 0)
  return {
    date: `Mar ${dayNum}`,
    dayNum,
    weekday,
    runs,
    totalHours: Math.round(totalHours * 10) / 10,
    hasAlert,
  }
}

export const DEMO_DAYS: DayEvents[] = [
  // Week 1
  makeDayEvents(1, "Sat", halfday(0), false),
  makeDayEvents(2, "Sun", [], false),
  makeDayEvents(3, "Mon", workday(0), false),
  makeDayEvents(4, "Tue", workday(1), false),
  makeDayEvents(5, "Wed", workday(2), false),
  makeDayEvents(6, "Thu", workday(0), false),
  makeDayEvents(7, "Fri", workday(1), false),
  // Week 2
  makeDayEvents(8, "Sat", halfday(1), false),
  makeDayEvents(9, "Sun", [], false),
  makeDayEvents(10, "Mon", workday(2), false),
  makeDayEvents(11, "Tue", workday(0), false),
  makeDayEvents(12, "Wed", workday(1), false),
  makeDayEvents(13, "Thu", workday(2), false),
  makeDayEvents(14, "Fri", workday(0), false),
  // Week 3
  makeDayEvents(15, "Sat", halfday(2), false),
  makeDayEvents(16, "Sun", [], false),
  makeDayEvents(17, "Mon", workday(1), false),
  makeDayEvents(18, "Tue", workday(0), true),  // fuel alert
  makeDayEvents(19, "Wed", workday(2), false),
  makeDayEvents(20, "Thu", workday(1), true),   // unknown engine alert
  makeDayEvents(21, "Fri", workday(0), false),
  // Week 4
  makeDayEvents(22, "Sat", halfday(0), true),   // runtime discrepancy
  makeDayEvents(23, "Sun", [], false),
  makeDayEvents(24, "Mon", workday(2), false),
  makeDayEvents(25, "Tue", [  // THE anomaly day
    ...workday(1),
    { engineId: "generator", engineName: "Generator #1", start: mins(23, 0), end: mins(26, 15), anomaly: true },  // 11PM-2:15AM (next day)
  ], true),
  makeDayEvents(26, "Wed", [  // rain — shorter day
    { engineId: "generator", engineName: "Generator #1", start: mins(6, 15), end: mins(14, 30) },
    { engineId: "waterpump", engineName: "Water Pump", start: mins(7, 20), end: mins(13, 45) },
    { engineId: "sluicepump", engineName: "Sluice Pump", start: mins(7, 30), end: mins(12, 10) },
  ], false),
  makeDayEvents(27, "Thu", [  // today — partial
    { engineId: "generator", engineName: "Generator #1", start: mins(6, 5), end: mins(14, 23) },
    { engineId: "waterpump", engineName: "Water Pump", start: mins(7, 12), end: mins(14, 0) },
    { engineId: "sluicepump", engineName: "Sluice Pump", start: mins(7, 18), end: mins(12, 50) },
    { engineId: "excavator", engineName: "Excavator", start: mins(7, 30), end: mins(11, 15) },
  ], false),
  makeDayEvents(28, "Fri", [], false),  // future
  makeDayEvents(29, "Sat", [], false),
  makeDayEvents(30, "Sun", [], false),
]

// Work window for the time axis (5am-7pm covers all realistic activity)
export const TIMELINE_START = 5 * 60   // 5:00 AM in minutes
export const TIMELINE_END = 19 * 60    // 7:00 PM in minutes
export const TIMELINE_RANGE = TIMELINE_END - TIMELINE_START

// For the overview page: today is Mar 27
export const DEMO_TODAY = DEMO_DAYS.find((d) => d.dayNum === 27)!

// ── Alerts ───────────────────────────────────────────────────────────────────

export const DEMO_ALERTS = [
  {
    id: "demo-alert-1",
    siteId: DEMO_SITE.id,
    type: "after_hours" as const,
    severity: "high" as const,
    message: "Unscheduled activity: Generator ran 11:00 PM – 2:15 AM",
    engineId: "generator",
    acknowledged: false,
    createdAt: new Date("2026-03-25T02:15:00"),
  },
  {
    id: "demo-alert-2",
    siteId: DEMO_SITE.id,
    type: "fuel" as const,
    severity: "medium" as const,
    message: "Runtime discrepancy: reported 14h, recorded 9h",
    engineId: "generator",
    acknowledged: false,
    createdAt: new Date("2026-03-22T18:00:00"),
  },
  {
    id: "demo-alert-3",
    siteId: DEMO_SITE.id,
    type: "anomaly" as const,
    severity: "medium" as const,
    message: "Unknown engine detected by Node C",
    engineId: null,
    acknowledged: true,
    createdAt: new Date("2026-03-20T19:40:00"),
  },
  {
    id: "demo-alert-4",
    siteId: DEMO_SITE.id,
    type: "fuel" as const,
    severity: "low" as const,
    message: "Fuel estimate exceeded weekly average by 18%",
    engineId: "waterpump",
    acknowledged: true,
    createdAt: new Date("2026-03-18T17:00:00"),
  },
]

// ── Fuel calculations ────────────────────────────────────────────────────────

export const FUEL_PRICE_USD = 1.70 // Interior diesel ~GYD $350/L

export const DEMO_WEEKLY_FUEL = DEMO_ENGINES.map((e) => {
  const weekHours = DEMO_WEEK_HOURS[e.engineId as keyof typeof DEMO_WEEK_HOURS] || 0
  const liters = Math.round(weekHours * e.fuelRate)
  return {
    name: e.name,
    type: e.engineType,
    fuelRate: e.fuelRate,
    weekHours,
    liters,
    cost: Math.round(liters * FUEL_PRICE_USD),
  }
})

export const DEMO_TOTAL_WEEKLY_FUEL = DEMO_WEEKLY_FUEL.reduce((a, b) => a + b.liters, 0)
export const DEMO_TOTAL_WEEKLY_COST = DEMO_WEEKLY_FUEL.reduce((a, b) => a + b.cost, 0)

// ── Alert type/severity helpers ──────────────────────────────────────────────

export const SEVERITY_COLORS: Record<string, string> = {
  high: "bg-red-600 hover:bg-red-600 text-white",
  medium: "bg-yellow-600 hover:bg-yellow-600 text-white",
  low: "bg-blue-600 hover:bg-blue-600 text-white",
}

export const ALERT_TYPE_LABELS: Record<string, string> = {
  after_hours: "After Hours",
  fuel: "Fuel",
  anomaly: "Anomaly",
}

// ── Fuel Purchases (manual entries) ──────────────────────────────────────────

export interface FuelPurchase {
  id: string
  date: Date
  liters: number
  note: string
}

export const DEMO_FUEL_PURCHASES: FuelPurchase[] = [
  { id: "fp1", date: new Date("2026-03-03"), liters: 1550, note: "Weekly delivery" },
  { id: "fp2", date: new Date("2026-03-10"), liters: 1600, note: "Weekly delivery" },
  { id: "fp3", date: new Date("2026-03-17"), liters: 1650, note: "Weekly delivery" },
  { id: "fp4", date: new Date("2026-03-24"), liters: 1650, note: "Weekly delivery" },
]

// Unit conversion
export const LITERS_PER_GALLON = 3.78541

// Build cumulative chart data: estimated consumption vs purchases over time
export function buildCumulativeChartData(purchases: FuelPurchase[]) {
  let cumEstimated = 0
  let cumPurchased = 0
  const data: { date: string; dayNum: number; estimated: number; purchased: number }[] = []

  for (const day of DEMO_DAYS) {
    // Add daily estimated consumption
    for (const run of day.runs) {
      if (run.anomaly) continue
      const hours = (run.end - run.start) / 60
      const engine = DEMO_ENGINES.find((e) => e.engineId === run.engineId)
      if (engine) cumEstimated += hours * engine.fuelRate
    }

    // Add purchases that happened on this day
    for (const p of purchases) {
      if (p.date.getDate() === day.dayNum && p.date.getMonth() === 2) { // March
        cumPurchased += p.liters
      }
    }

    if (day.totalHours > 0 || cumPurchased > 0) {
      data.push({
        date: `${day.dayNum}`,
        dayNum: day.dayNum,
        estimated: Math.round(cumEstimated),
        purchased: Math.round(cumPurchased),
      })
    }
  }

  return data
}

// ── Period aggregation helpers ───────────────────────────────────────────────

export type Period = "today" | "week" | "month"

// Current week = Mon Mar 24 - Thu Mar 27 (today)
const WEEK_DAYS = DEMO_DAYS.filter((d) => d.dayNum >= 24 && d.dayNum <= 27)
const MONTH_DAYS = DEMO_DAYS.filter((d) => d.totalHours > 0)

function aggregateByPeriod(days: DayEvents[]) {
  const engineHours: Record<string, number> = {}
  for (const day of days) {
    for (const run of day.runs) {
      if (run.anomaly) continue
      const h = (run.end - run.start) / 60
      engineHours[run.engineId] = (engineHours[run.engineId] || 0) + h
    }
  }
  // Round to 1 decimal
  for (const k of Object.keys(engineHours)) {
    engineHours[k] = Math.round(engineHours[k] * 10) / 10
  }
  return engineHours
}

function fuelForPeriod(engineHours: Record<string, number>) {
  return DEMO_ENGINES.map((e) => {
    const hours = engineHours[e.engineId] || 0
    const liters = Math.round(hours * e.fuelRate)
    return {
      engineId: e.engineId,
      name: e.name,
      type: e.engineType,
      fuelRate: e.fuelRate,
      hours,
      liters,
      cost: Math.round(liters * FUEL_PRICE_USD),
    }
  })
}

export function getPeriodData(period: Period) {
  const days = period === "today" ? [DEMO_TODAY] : period === "week" ? WEEK_DAYS : MONTH_DAYS
  const engineHours = aggregateByPeriod(days)
  const fuel = fuelForPeriod(engineHours)
  const totalHours = Object.values(engineHours).reduce((a, b) => a + b, 0)
  const totalFuel = fuel.reduce((a, b) => a + b.liters, 0)
  const totalCost = fuel.reduce((a, b) => a + b.cost, 0)
  const chartData = days.map((d) => ({
    date: d.date.replace("Mar ", ""),
    hours: d.totalHours,
  }))

  return { engineHours, fuel, totalHours, totalFuel, totalCost, chartData, days }
}
